/**
 * MAKING A PHOTO SMALL ENOUGH TO SEND
 * -----------------------------------
 * A profile picture ends up as a 400x400 circle. A photo taken on a phone is
 * commonly 3-6MB and four thousand pixels wide, so almost all of what gets
 * uploaded is thrown away the moment it arrives.
 *
 * That was not just waste - it was a bug. The upload limit was 2MB, which a
 * normal phone photo exceeds, so "take a picture and use it" was refused.
 * The limit is 8MB now, but sending 6MB over a school phone's data
 * connection to produce a thumbnail is still the wrong thing to do, and it is
 * slow exactly where slowness is most noticeable.
 *
 * So the browser shrinks the picture first, and sends something small.
 *
 * WHAT THIS ALSO FIXES, ALMOST BY ACCIDENT
 * ----------------------------------------
 * An iPhone shoots HEIC by default. The server only accepts JPG, PNG, WEBP
 * and GIF, so a photo straight off an iPhone could be refused for its format
 * as well as its size. Anything that goes through here comes out as a JPEG,
 * whatever it went in as, because that is what drawing it and re-saving it
 * does. Safari can read HEIC, so on the phone where it matters this works.
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 * --------------------------------
 * A file that is already small enough is passed through untouched. Two
 * reasons: there is nothing to gain, and re-saving an animated GIF through a
 * canvas would silently flatten it to its first frame. A large animated GIF
 * does get flattened - by then it is that or refuse it, and a still picture
 * is the better answer.
 *
 * If anything here fails, the original file is returned and the upload is
 * tried anyway. This is an optimisation on the way to a server that still
 * checks the size and the format itself; it is not a gate, and it must never
 * be the reason somebody cannot add a picture.
 */

// The longest side of the picture we send. The server crops to 400x400, so
// 1000 leaves room for a larger crop later without keeping a photo that is
// four thousand pixels wide for a thumbnail.
const MAX_EDGE = 1000;

// What we aim to come in under. Far below the server's 8MB limit on purpose:
// this is about the upload being quick on a phone, not about squeezing past
// a check.
const TARGET_BYTES = 1024 * 1024;

// Tried in order until one comes in under the target. JPEG quality is not a
// percentage of anything meaningful - 0.85 is visually indistinguishable from
// the original at this size, and 0.55 is still perfectly good in a circle
// 400 pixels across.
const QUALITIES = [0.85, 0.7, 0.55];

// Formats the server accepts as they are. Anything else - HEIC, chiefly - is
// converted even when it is small enough, because otherwise it is refused.
const PASS_THROUGH = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

/**
 * Reads the file into something we can draw.
 *
 * createImageBitmap is the good path: it is fast, and `from-image` applies
 * the rotation flag a phone writes into a photo instead of ignoring it. Miss
 * that and every picture taken in portrait arrives on its side - the camera
 * does not rotate the pixels, it writes down which way up they go.
 *
 * The fallback loads it as an ordinary image element, which modern browsers
 * also rotate correctly. It needs a blob: URL, which is why blob: is in the
 * image policy in backend/server.js.
 */
async function loadImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file, { imageOrientation: 'from-image' });
    } catch {
      // Older Safari knows createImageBitmap but not the options. Fall through.
    }
  }

  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('This image could not be read.'));
      img.src = url;
    });
  } finally {
    // Frees the memory the blob: URL is holding. Without this a few goes at
    // choosing a photo keep several full-size images alive for the life of
    // the page.
    URL.revokeObjectURL(url);
  }
}

/** Promise-shaped canvas.toBlob, which is otherwise callback-only. */
function toBlob(canvas, quality) {
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

/**
 * Shrinks a photo if it needs it. Always resolves - with the original file if
 * anything at all goes wrong.
 *
 * @param {File} file  whatever the person chose
 * @returns {Promise<File>}
 */
export async function shrinkImage(file) {
  const alreadyFine = file.size <= TARGET_BYTES && PASS_THROUGH.includes(file.type);
  if (alreadyFine) return file;

  // A browser too old for either of these gets the original and the server's
  // own limit, which is the behaviour this function replaced.
  if (typeof document === 'undefined' || !document.createElement('canvas').getContext) {
    return file;
  }

  try {
    const source = await loadImage(file);
    const width = source.width;
    const height = source.height;
    if (!width || !height) return file;

    // Never enlarge: a small picture stays its own size. Scaling a 200px
    // avatar up to 1000 makes a bigger, blurrier file and nothing else.
    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);

    const ctx = canvas.getContext('2d');
    // A photo shrunk with the default setting comes out visibly gritty.
    ctx.imageSmoothingQuality = 'high';
    // JPEG has no transparency, so a PNG with a see-through background would
    // otherwise be drawn onto black. White is what the site's cards are.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(source, 0, 0, canvas.width, canvas.height);

    // Frees the decoded picture straight away rather than waiting for the
    // browser to get round to it. A phone doing this on a big photo is close
    // enough to its memory limit for it to matter.
    if (typeof source.close === 'function') source.close();

    let best = null;
    for (const quality of QUALITIES) {
      // eslint-disable-next-line no-await-in-loop -- each attempt depends on
      // whether the previous one came in small enough.
      const blob = await toBlob(canvas, quality);
      if (!blob) break;
      best = blob;
      if (blob.size <= TARGET_BYTES) break;
    }

    if (!best) return file;

    // If all that made it bigger, keep the original. This happens with a
    // small, heavily optimised PNG - a logo rather than a photograph.
    if (best.size >= file.size && PASS_THROUGH.includes(file.type)) return file;

    // The name has to end in .jpg, because the server names the stored file
    // from the extension it was given. Called photo.heic it would be written
    // to disk as a .heic file containing a JPEG, and nothing would display it.
    const name = `${(file.name || 'photo').replace(/\.[^./\\]+$/, '')}.jpg`;

    return new File([best], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    // Deliberately silent. The upload still happens with the original file,
    // and the server says something useful if it is genuinely too big.
    return file;
  }
}

export default shrinkImage;
